import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import styles from "./cssmodules/PermissionChecker.module.css";
import {
  CheckCircleOutlined,
  CancelOutlined,
  SearchOutlined,
  FilterListOutlined,
} from "@mui/icons-material";

const PermissionChecker = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, granted, missing
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [userInfo, setUserInfo] = useState({});

  // Get user permissions from Redux store
  const userPermissions = useSelector(
    (state) => state.auth.loggedInUserRolePermissions || []
  );

  // Expected permissions structure based on MenuData
  const expectedPermissions = {
    Dashboard: ["View"],
    MembershipApplication: ["View", "Create", "Update", "Delete"],
    MembershipRequests: ["View", "Create", "Update", "Delete"],
    MembershipApproval: ["View", "Approve", "Reject", "Update"],
    MembershipDirectory: ["View", "Create", "Update", "Delete", "Export"],
    AccessRequests: ["View", "Approve", "Reject", "Delete"],
    AdminManagement: ["View"],
    ManageRole: ["View", "Create", "Update", "Delete"],
    ManageUser: ["View", "Create", "Update", "Delete", "ResetPassword"],
    MasterList: ["View", "Create", "Update", "Delete"],
    WorkflowManagement: ["View", "Create", "Update", "Delete"],
    MembershipTierManagement: ["View", "Create", "Update", "Delete"],
    Payments: ["View", "Create", "Update", "Delete", "Export"],
    MyPayments: ["View", "Export"],
    AllPayments: ["View", "Create", "Update", "Delete", "Export"],
    BugReporting: ["View", "Create"],
    SendFeedback: ["View", "Create"],
    Support: ["View", "Create"],
  };

  useEffect(() => {
    // Get user info from localStorage
    const firstName = localStorage.getItem("loggedInUserFirstName");
    const lastName = localStorage.getItem("loggedInUserLastName");
    const email = localStorage.getItem("loggedInUserEmail");
    const roleName = localStorage.getItem("loggedInUserRoleName");
    const businessName = localStorage.getItem("loggedInUserBusinessName");

    setUserInfo({
      firstName,
      lastName,
      email,
      roleName,
      businessName,
    });

    // Group permissions by category
    const grouped = {};

    Object.keys(expectedPermissions).forEach((category) => {
      const actions = expectedPermissions[category];
      grouped[category] = actions.map((action) => {
        const hasPermission = userPermissions.some(
          (perm) =>
            perm.permissionCategoryName === category &&
            perm.permissionName === action
        );

        return {
          action,
          granted: hasPermission,
        };
      });
    });

    setGroupedPermissions(grouped);
  }, [userPermissions]);

  // Check if user has a specific permission
  const hasPermission = (category, action) => {
    return userPermissions.some(
      (perm) =>
        perm.permissionCategoryName === category &&
        perm.permissionName === action
    );
  };

  // Filter permissions based on search and filter type
  const getFilteredPermissions = () => {
    const filtered = {};

    Object.keys(groupedPermissions).forEach((category) => {
      if (
        searchTerm &&
        !category.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return;
      }

      const permissions = groupedPermissions[category];
      let filteredPermissions = permissions;

      if (filterType === "granted") {
        filteredPermissions = permissions.filter((p) => p.granted);
      } else if (filterType === "missing") {
        filteredPermissions = permissions.filter((p) => !p.granted);
      }

      if (filteredPermissions.length > 0) {
        filtered[category] = filteredPermissions;
      }
    });

    return filtered;
  };

  const filteredPermissions = getFilteredPermissions();

  // Calculate statistics
  const totalExpected = Object.values(expectedPermissions).reduce(
    (sum, actions) => sum + actions.length,
    0
  );
  const totalGranted = userPermissions.length;
  const percentageGranted = ((totalGranted / totalExpected) * 100).toFixed(1);

  const exportPermissions = () => {
    const data = {
      userInfo,
      permissions: userPermissions,
      timestamp: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `permissions-${userInfo.email || "user"}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Permission Checker</h1>
        <p className={styles.subtitle}>
          View your current permissions and identify missing access rights
        </p>
      </div>

      {/* User Info Card */}
      <div className={styles.userInfoCard}>
        <h2 className={styles.sectionTitle}>Your Information</h2>
        <div className={styles.userInfoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Name:</span>
            <span className={styles.infoValue}>
              {userInfo.firstName} {userInfo.lastName}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Email:</span>
            <span className={styles.infoValue}>{userInfo.email}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Role:</span>
            <span className={styles.infoValue}>{userInfo.roleName}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Business:</span>
            <span className={styles.infoValue}>{userInfo.businessName}</span>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{totalGranted}</div>
          <div className={styles.statLabel}>Granted Permissions</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{totalExpected - totalGranted}</div>
          <div className={styles.statLabel}>Missing Permissions</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{percentageGranted}%</div>
          <div className={styles.statLabel}>Coverage</div>
        </div>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.searchBox}>
          <SearchOutlined className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterButtons}>
          <button
            className={`${styles.filterBtn} ${filterType === "all" ? styles.active : ""}`}
            onClick={() => setFilterType("all")}
          >
            All
          </button>
          <button
            className={`${styles.filterBtn} ${filterType === "granted" ? styles.active : ""}`}
            onClick={() => setFilterType("granted")}
          >
            Granted
          </button>
          <button
            className={`${styles.filterBtn} ${filterType === "missing" ? styles.active : ""}`}
            onClick={() => setFilterType("missing")}
          >
            Missing
          </button>
        </div>

        <button className={styles.exportBtn} onClick={exportPermissions}>
          Export Permissions
        </button>
      </div>

      {/* Permissions List */}
      <div className={styles.permissionsContainer}>
        {Object.keys(filteredPermissions).length === 0 ? (
          <div className={styles.noResults}>
            <p>No permissions found matching your criteria.</p>
          </div>
        ) : (
          Object.keys(filteredPermissions).map((category) => (
            <div key={category} className={styles.permissionCategory}>
              <div className={styles.categoryHeader}>
                <h3 className={styles.categoryName}>{category}</h3>
                <span className={styles.categoryStats}>
                  {
                    filteredPermissions[category].filter((p) => p.granted)
                      .length
                  }{" "}
                  / {filteredPermissions[category].length}
                </span>
              </div>
              <div className={styles.permissionsList}>
                {filteredPermissions[category].map((perm) => (
                  <div
                    key={perm.action}
                    className={`${styles.permissionItem} ${perm.granted ? styles.granted : styles.missing}`}
                  >
                    {perm.granted ? (
                      <CheckCircleOutlined className={styles.iconGranted} />
                    ) : (
                      <CancelOutlined className={styles.iconMissing} />
                    )}
                    <span className={styles.permissionAction}>
                      {perm.action}
                    </span>
                    <span className={styles.permissionStatus}>
                      {perm.granted ? "Granted" : "Missing"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Help Section */}
      <div className={styles.helpSection}>
        <h3 className={styles.helpTitle}>Need More Permissions?</h3>
        <p className={styles.helpText}>
          If you need additional permissions, please contact your system
          administrator. Provide them with the following information:
        </p>
        <ul className={styles.helpList}>
          <li>Your email: <strong>{userInfo.email}</strong></li>
          <li>Your current role: <strong>{userInfo.roleName}</strong></li>
          <li>The specific permissions you need (listed above as "Missing")</li>
        </ul>
      </div>
    </div>
  );
};

export default PermissionChecker;
